// Meta Pixel — consent-gated loader + checkout conversion events.
//
// Consent comes from the `mp-consent` cookie the marketing site's cookie
// banner writes on `.marginpal.co.uk` (value: URL-encoded JSON {v,a,m}).
// A visitor who accepted Marketing cookies on www carries that consent here;
// anyone else gets NO pixel — measurement for them is covered server-side by
// the Meta Conversions API fired from the Stripe webhook (marginpal-api).
//
// Conversion: the API builds the Stripe success_url as
//   /dashboard?checkout=success&session_id={CHECKOUT_SESSION_ID}&tier=<tier>&trial=<0|1>
// We fire StartTrial (trial checkouts) or Subscribe (paid-from-day-one) with
// eventID = the checkout session id — the SAME id the server sends via CAPI,
// so Meta deduplicates the browser + server copies of the event.

const PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID;

// Keep in sync with subscribePage.jsx PLANS / ChangePlanModal PRICE_FOR_TIER.
const PRICE_FOR_TIER = { solo: 12, pro: 29, business: 59 };

function hasMarketingConsent() {
  try {
    const raw = document.cookie
      .split('; ')
      .find((c) => c.startsWith('mp-consent='));
    if (!raw) return false;
    const parsed = JSON.parse(decodeURIComponent(raw.slice('mp-consent='.length)));
    return parsed?.m === 1;
  } catch {
    return false;
  }
}

function loadPixel() {
  if (window.fbq) return;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', PIXEL_ID);
  window.fbq('track', 'PageView');
}

// Fires the checkout conversion if this page load is the Stripe success
// redirect. Guarded per session id in localStorage so a refresh of the
// landing URL can't double-fire, and the params are stripped afterwards so
// the URL the user sees (and bookmarks) is clean.
function trackCheckoutConversion() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('checkout') !== 'success') return;

  const sessionId = params.get('session_id');
  const tier = params.get('tier');
  const isTrial = params.get('trial') === '1';
  if (!sessionId) return;

  const guardKey = `mp-conv-${sessionId}`;
  try {
    if (localStorage.getItem(guardKey)) return;
    localStorage.setItem(guardKey, '1');
  } catch {
    /* storage unavailable — fire anyway, CAPI dedup still applies */
  }

  const value = PRICE_FOR_TIER[tier] ?? PRICE_FOR_TIER.business;
  window.fbq(
    'track',
    isTrial ? 'StartTrial' : 'Subscribe',
    { value, currency: 'GBP', predicted_ltv: value },
    { eventID: sessionId }
  );

  // Tidy the URL: keep the route, drop the tracking params.
  params.delete('checkout');
  params.delete('session_id');
  params.delete('tier');
  params.delete('trial');
  const qs = params.toString();
  window.history.replaceState(
    null,
    '',
    window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash
  );
}

/**
 * Boot the pixel. Call once, before React renders, so the success-redirect
 * URL is read before the router gets a chance to navigate away from it.
 */
export function initMetaPixel() {
  if (!PIXEL_ID) return;
  if (!hasMarketingConsent()) return;
  loadPixel();
  trackCheckoutConversion();
}

/**
 * Fire the StartTrial conversion when a new no-card trial is created (i.e. the
 * user just finished creating their org). This is the top-of-funnel conversion
 * the ad campaign optimises on — the old checkout-based StartTrial is gone,
 * since a trial no longer involves a Stripe checkout. eventID = the new org id,
 * matching the server CAPI copy fired from /api/Onboarding/CreateOrg, so Meta
 * dedups the browser + server pair. No-op without the pixel (no consent / not
 * configured); guarded per org id so a re-render can't double-fire.
 */
export function trackTrialStarted(orgId) {
  if (!window.fbq || !orgId) return;

  const guardKey = `mp-trial-${orgId}`;
  try {
    if (localStorage.getItem(guardKey)) return;
    localStorage.setItem(guardKey, '1');
  } catch {
    /* storage unavailable — fire anyway, CAPI dedup still applies */
  }

  const value = PRICE_FOR_TIER.business; // trial grants the Business feature set
  window.fbq(
    'track',
    'StartTrial',
    { value, currency: 'GBP', predicted_ltv: value },
    { eventID: orgId }
  );
}
